import { joinURL } from 'ufo'

import { API_DISALLOWED_FILETYPES } from '@layers/web/constants/api-fetch.ts'
import localStorageKeys from '@layers/web/constants/local-storage-keys.ts'
import type { ApiOptionalOptions, RequestOptions } from '@layers/web/types/api-fetch.ts'
import type { AgentCookie } from '@layers/web/types/root.ts'

const isValidFileType = (request: string, options: RequestOptions): boolean => {
  if (API_DISALLOWED_FILETYPES.some(filetype => request.endsWith(filetype))) {
    if (options.throwOnFail) {
      throw new Error('Not found: ' + request)
    }

    return false
  }

  return true
}

const createRequestOptions = (options: ApiOptionalOptions): RequestOptions => {
  const requestOptions: RequestOptions = {
    throwOnFail: true,
    externalUrl: false,
    simple: false,
    timeout: options.timeout ?? 60 * 1000,
    ...options,
  }

  const optionsWithBody = createRequestBody(requestOptions)
  return optionsWithBody
}

const createRequestBody = (options: RequestOptions): RequestOptions => {
  if (options.method?.toUpperCase() !== 'POST' || options.addToken !== true) {
    return options
  }

  const token = window.localStorage.getItem(localStorageKeys.auth.token)

  if (!token) {
    return options
  }

  options.body = { token, ...options.body }
  return options
}

const createRequestHeaders = (options: RequestOptions): Headers => {
  const rootStore = useRootStore()
  const headers = new Headers()

  if (import.meta.browser) {
    headers.set('X-HREF', location.href)
  }

  if (options.simple) {
    return headers
  }

  const cookie = rootStore.rolfsCookie
  if (cookie) {
    headers.set('X-ROLFS-RF', cookie)
  }

  if (rootStore.agentCookie) {
    const agentCookie = rootStore.agentCookie as AgentCookie
    headers.set('X-ROLFS-A', agentCookie.code)
    headers.set('X-ROLFS-ATIME', agentCookie.currentDate)
  }

  return headers
}

const logHttpRequest = (request: string, options: RequestOptions): void => {
  const { API_BASE_PATH } = useConfigStore()
  if (process.env.NODE_ENV !== 'development') {
    return
  }

  console.info(
    options.externalUrl
      ? `[apiFetch] Making external request to: ${request}`
      : `[apiFetch] Making request to: ${API_BASE_PATH}${request}`
  )
}

const logFailedHttpRequest = (error: any, throwOnFail: boolean = false): any => {
  if (process.env.NODE_ENV === 'development') {
    console.error('[apiFetch] Request failed: ', error)
    return
  }

  if (!throwOnFail) {
    return error
  }

  throw error
}

const createApiUrl = (request: string, options: RequestOptions): string => {
  const { API_URL, API_BASE_PATH } = useConfigStore()
  const { externalUrl } = options

  if (externalUrl) {
    return request
  }

  if (import.meta.browser) {
    return `${API_BASE_PATH}${request}` // PROXY
  }

  return joinURL(API_URL, API_BASE_PATH) + request // API URI
}

const parseHttpResponseToCookie = (simple: boolean, response: Response): void => {
  const rootStore = useRootStore()
  if (simple || !response.headers.get('x-rolfs-rf') || rootStore.rolfsCookie) {
    return
  }

  const cookie = response.headers.get('x-rolfs-rf')

  rootStore.SET_ROLFSCOOKIE(cookie)
}

export {
  createApiUrl,
  createRequestBody,
  createRequestHeaders,
  createRequestOptions,
  isValidFileType,
  logFailedHttpRequest,
  logHttpRequest,
  parseHttpResponseToCookie,
}
